import { useState } from 'react';

import {
  replaceDocumentContent,
  syncDocuments,
  type DocumentSummary,
} from '../../../services/api/documents';
import { getApiErrorMessage } from '../../../utils/api-error';
import { planBulkReplace, summarizeBulkReplace, type BulkReplaceResult } from '../bulk-replace';

export const useBulkReplace = ({ onResult }: { onResult: (result: BulkReplaceResult) => void }) => {
  const [isBulkReplacing, setIsBulkReplacing] = useState(false);

  const runBulkReplace = async (files: File[], documents: DocumentSummary[]) => {
    if (files.length === 0) return;
    setIsBulkReplacing(true);

    const read = await Promise.all(
      files.map(async (file) => {
        try {
          return { name: file.name, text: await file.text() };
        } catch (error) {
          return { name: file.name, readError: getApiErrorMessage(error, 'okänt fel') };
        }
      }),
    );

    const unreadable = read.filter(
      (entry): entry is { name: string; readError: string } => 'readError' in entry,
    );
    const { ready, skipped } = planBulkReplace(
      read.filter((entry): entry is { name: string; text: string } => 'text' in entry),
      documents,
    );

    const skippedNames = [
      ...unreadable.map((entry) => `${entry.name} (kunde inte läsa fil: ${entry.readError})`),
      ...skipped.map((entry) => `${entry.name} (${entry.reason})`),
    ];
    const failedNames: string[] = [];
    let replaced = 0;

    for (const entry of ready) {
      try {
        await replaceDocumentContent(entry.id, new Blob([entry.text], { type: 'text/markdown' }), {
          filename: entry.name,
          skipSync: true,
        });
        replaced += 1;
      } catch (error) {
        failedNames.push(`${entry.name}: ${getApiErrorMessage(error, 'okänt fel')}`);
      }
    }

    // Synken körs en gång på slutet — därför skipSync på varje enskild ersättning.
    let syncError: string | undefined;
    if (replaced > 0) {
      try {
        // syncDocuments svarar 200 även när synken gick fel — felen kommer som en räknare.
        const result = await syncDocuments();
        if (result.errors > 0) {
          syncError = `${result.errors} dokument kunde inte synkas`;
        }
      } catch (error) {
        syncError = getApiErrorMessage(error, 'okänt fel');
      }
    }

    setIsBulkReplacing(false);
    onResult(
      summarizeBulkReplace({ replaced, skipped: skippedNames, failed: failedNames, syncError }),
    );
  };

  return { runBulkReplace, isBulkReplacing };
};
